import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Cake as CakeIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import FamilyMemberPasswordModal from './FamilyMemberPasswordModal';
import api from '../services/api';

interface FamilyMember {
  id: number;
  nom: string;
  prenom: string;
  date_naissance: string | null;
  lieu_naissance: string | null;
  relation: 'enfant' | 'conjoint' | 'autre';
  numero_passport: string | null;
  numero_cin: string | null;
  notes: string | null;
  nombre_demarches: number;
  nombre_reparations: number;
}

interface FamilyMembersModalProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  clientName: string;
}

const FamilyMembersModal: React.FC<FamilyMembersModalProps> = ({
  open,
  onClose,
  clientId,
  clientName,
}) => {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<FamilyMember | null>(null);

  // Formulaire
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    lieu_naissance: '',
    relation: 'enfant' as 'enfant' | 'conjoint' | 'autre',
    numero_passport: '',
    numero_cin: '',
    notes: '',
  });

  useEffect(() => {
    if (open) {
      loadMembers();
      setShowAddForm(false);
      setEditingMember(null);
    }
  }, [open, clientId]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/clients/${clientId}/family-members`);
      setMembers(response.data.data);
    } catch (error) {
      console.error('Error loading family members:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      date_naissance: '',
      lieu_naissance: '',
      relation: 'enfant',
      numero_passport: '',
      numero_cin: '',
      notes: '',
    });
  };

  const handleAdd = async () => {
    if (!formData.nom || !formData.prenom) return;

    setLoading(true);
    try {
      await api.post(`/clients/${clientId}/family-members`, formData);
      await loadMembers();
      resetForm();
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding family member:', error);
      alert('Erreur lors de l\'ajout du membre');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingMember || !formData.nom || !formData.prenom) return;

    setLoading(true);
    try {
      await api.put(`/clients/${clientId}/family-members/${editingMember.id}`, formData);
      await loadMembers();
      resetForm();
      setEditingMember(null);
    } catch (error) {
      console.error('Error updating family member:', error);
      alert('Erreur lors de la modification du membre');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (memberId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre de la famille ?')) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/clients/${clientId}/family-members/${memberId}`);
      await loadMembers();
    } catch (error) {
      console.error('Error deleting family member:', error);
      alert('Erreur lors de la suppression du membre');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (member: FamilyMember) => {
    setFormData({
      nom: member.nom,
      prenom: member.prenom,
      date_naissance: member.date_naissance || '',
      lieu_naissance: member.lieu_naissance || '',
      relation: member.relation,
      numero_passport: member.numero_passport || '',
      numero_cin: member.numero_cin || '',
      notes: member.notes || '',
    });
    setEditingMember(member);
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingMember(null);
    resetForm();
  };

  const getRelationLabel = (relation: string) => {
    switch (relation) {
      case 'enfant': return 'Enfant';
      case 'conjoint': return 'Conjoint(e)';
      case 'autre': return 'Autre';
      default: return relation;
    }
  };

  const getRelationColor = (relation: string): 'primary' | 'secondary' | 'default' => {
    switch (relation) {
      case 'enfant': return 'primary';
      case 'conjoint': return 'secondary';
      default: return 'default';
    }
  };

  if (loading && members.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Typography>Chargement...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Gestion de la famille
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {clientName}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 400 }}>
        {/* En-tête avec bouton Ajouter */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Membres de la famille ({members.length})
          </Typography>
          {!showAddForm && !editingMember && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setShowAddForm(true);
              }}
              size="small"
            >
              Ajouter un membre
            </Button>
          )}
        </Box>

        {/* Formulaire d'ajout/modification */}
        {(showAddForm || editingMember) && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {editingMember ? 'Modifier le membre' : 'Nouveau membre'}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setShowAddForm(false);
                  cancelEdit();
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="Nom *"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="Prénom *"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Relation *</InputLabel>
                  <Select
                    value={formData.relation}
                    label="Relation *"
                    onChange={(e) => setFormData({ ...formData, relation: e.target.value as any })}
                  >
                    <MenuItem value="enfant">Enfant</MenuItem>
                    <MenuItem value="conjoint">Conjoint(e)</MenuItem>
                    <MenuItem value="autre">Autre</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="Date de naissance"
                  type="date"
                  value={formData.date_naissance}
                  onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="Lieu de naissance"
                  value={formData.lieu_naissance}
                  onChange={(e) => setFormData({ ...formData, lieu_naissance: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="Numéro de passeport"
                  value={formData.numero_passport}
                  onChange={(e) => setFormData({ ...formData, numero_passport: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="Numéro CIN"
                  value={formData.numero_cin}
                  onChange={(e) => setFormData({ ...formData, numero_cin: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={editingMember ? handleUpdate : handleAdd}
                    disabled={!formData.nom || !formData.prenom || loading}
                  >
                    {editingMember ? 'Modifier' : 'Ajouter'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowAddForm(false);
                      cancelEdit();
                    }}
                  >
                    Annuler
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Liste des membres */}
        {members.length > 0 ? (
          <List>
            {members.map((member) => (
              <Paper key={member.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PersonIcon color="action" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {member.prenom} {member.nom}
                      </Typography>
                      <Chip
                        label={getRelationLabel(member.relation)}
                        color={getRelationColor(member.relation)}
                        size="small"
                      />
                    </Box>

                    <Grid container spacing={1}>
                      {member.date_naissance && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CakeIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              Né(e) le {new Date(member.date_naissance).toLocaleDateString('fr-FR')}
                              {member.lieu_naissance && ` à ${member.lieu_naissance}`}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {(member.numero_passport || member.numero_cin) && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            {member.numero_passport && `Passeport: ${member.numero_passport}`}
                            {member.numero_passport && member.numero_cin && ' • '}
                            {member.numero_cin && `CIN: ${member.numero_cin}`}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    {/* Statistiques des dossiers */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      {member.nombre_demarches > 0 && (
                        <Chip
                          icon={<AssignmentIcon />}
                          label={`${member.nombre_demarches} démarche${member.nombre_demarches > 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {member.nombre_reparations > 0 && (
                        <Chip
                          icon={<BuildIcon />}
                          label={`${member.nombre_reparations} réparation${member.nombre_reparations > 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {member.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        {member.notes}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedMember(member);
                        setPasswordModalOpen(true);
                      }}
                      color="info"
                      title="Gérer les mots de passe"
                    >
                      <VpnKeyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => startEdit(member)}
                      color="primary"
                      disabled={editingMember?.id === member.id}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(member.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </List>
        ) : (
          !showAddForm && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Aucun membre de famille enregistré
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setShowAddForm(true);
                }}
                sx={{ mt: 2 }}
              >
                Ajouter un membre
              </Button>
            </Paper>
          )
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>

      {/* Modal de gestion des mots de passe pour un membre */}
      {selectedMember && (
        <FamilyMemberPasswordModal
          open={passwordModalOpen}
          onClose={() => {
            setPasswordModalOpen(false);
            setSelectedMember(null);
          }}
          familyMemberId={selectedMember.id}
          memberName={`${selectedMember.prenom} ${selectedMember.nom}`}
        />
      )}
    </Dialog>
  );
};

export default FamilyMembersModal;
